import type { PickSampleExt } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { selectPicker } from "features/render";
import { getSurfaceProjectionTransform } from "features/render/svgUtils";
import { Picker } from "features/render/types";
import { hasMouseSupport } from "utils/misc";

import type { CursorVisual } from "./mobileMeasureSlice";
import {
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
    selectCursorActive,
    selectCursorPosition,
} from "./mobileMeasureSlice";

export interface MobileCursorCallbacks {
    picker: Picker;
    onPlace: (result: PickSampleExt, canvasPos: { x: number; y: number }) => void;
    onDragUpdate?: (result: PickSampleExt | null) => void;
    /** Called in persistent mode when tapping outside the D-pad area. */
    onTapElsewhere?: (canvasPos: { x: number; y: number }) => void;
    /** Called in persistent mode when D-pad is tapped without significant drag. */
    onDpadTap?: () => void;
    /** Called when D-pad is held without movement for 400ms (long-press). */
    onDpadLongPress?: () => void;
    /**
     * Called in persistent mode when a canvas-area press away from the D-pad ends as a
     * stationary single-finger tap. Unlike `onTapElsewhere`, which fires on touchstart,
     * this only fires once the gesture is known not to have become a drag, a pinch or a
     * long-press — so it is safe for destructive actions.
     */
    onCanvasTap?: (canvasPos: { x: number; y: number }) => void;
    /** Called when a canvas-area press away from the D-pad is held still for 400ms. */
    onCanvasLongPress?: (canvasPos: { x: number; y: number }) => void;
    /**
     * Called when a gesture the hook had claimed is abandoned because a second finger
     * arrived. The hook rolls back its own state, but a move the long-press already armed
     * lives in the tool, so the tool has to clear it here or the next release will apply
     * it. Nothing is placed and no measurement is completed.
     */
    onAbort?: () => void;
    enabled?: boolean;
    /** Picker radius for view.pick; use smaller value for edge-sensitive tools. */
    sampleDiscRadius?: number;
    /** Override hit validity when objectId === -1 can still be actionable. */
    isValidPick?: (result: PickSampleExt | null) => boolean;
    /**
     * In persistent mode, treat short D-pad tap as `onDpadTap` instead of placing.
     * Useful for PointLine mode transitions (arrow <-> transparent).
     */
    dpadTapCancelsPlace?: boolean;
    /**
     * When true, the cursor persists after placement:
     * - First tap places the cursor (no measurement).
     * - Drag from D-pad area moves cursor; release creates measurement.
     * - Cursor stays visible between measurements.
     */
    persistent?: boolean;
}

const DPAD_HIT_RADIUS = 36;
const DPAD_DRAG_THRESHOLD_SQ = 8 * 8;
const DPAD_LONG_PRESS_MS = 400;

export function useMobileMeasureCursor({
    picker,
    onPlace,
    onDragUpdate,
    onTapElsewhere,
    onDpadTap,
    onDpadLongPress,
    onCanvasTap,
    onCanvasLongPress,
    onAbort,
    enabled = true,
    sampleDiscRadius = 8,
    isValidPick,
    dpadTapCancelsPlace = false,
    persistent = false,
}: MobileCursorCallbacks) {
    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const {
        state: { view, canvas, size },
    } = useExplorerGlobals();

    const currentPicker = useAppSelector(selectPicker);
    const cursorActive = useAppSelector(selectCursorActive);
    const cursorPosition = useAppSelector(selectCursorPosition);

    const lastPickRef = useRef<PickSampleExt | null>(null);
    const lastCanvasPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const pickingRef = useRef(false);
    /** Bumped when a gesture is abandoned, so a pick still in flight can be discarded. */
    const pickGenerationRef = useRef(0);
    const touchActiveRef = useRef(false);
    const cursorPlacedRef = useRef(false);
    const draggingRef = useRef(false);
    const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const dragMovedRef = useRef(false);
    /** D-pad position before a drag claimed it, so an aborted gesture can put it back. */
    const dpadOriginRef = useRef<{ x: number; y: number } | null>(null);
    const dpadLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dpadLongPressFiredRef = useRef(false);
    const canvasPressStartRef = useRef<{ x: number; y: number } | null>(null);
    const canvasLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const canvasLongPressFiredRef = useRef(false);
    const wasActiveRef = useRef(false);
    const sizeRef = useRef(size);
    sizeRef.current = size;

    const isActive = enabled && !hasMouseSupport() && currentPicker === picker;

    const isPickValid = useCallback(
        (result: PickSampleExt | null) => {
            if (!result) {
                return false;
            }
            if (isValidPick) {
                return isValidPick(result);
            }
            return result.objectId !== -1;
        },
        [isValidPick],
    );

    const determineCursorVisual = useCallback(
        (result: PickSampleExt | null): CursorVisual => {
            if (!isPickValid(result)) {
                return "noHit";
            }

            // Match desktop semantics for Dimension hover:
            // - edge -> 2-sided cursor
            // - surface/corner -> 4-sided cursor
            if (picker === Picker.Dimension && result?.sampleType === "edge") {
                return "pointSnap";
            }

            return result?.sampleType === "surface" ? "surfaceSnap" : "pointSnap";
        },
        [isPickValid, picker],
    );

    const doPick = useCallback(
        async (
            x: number,
            y: number,
            { visualOnly = false, asyncPick = false }: { visualOnly?: boolean; asyncPick?: boolean } = {},
        ) => {
            if (!view || pickingRef.current) {
                return;
            }
            const generation = pickGenerationRef.current;
            pickingRef.current = true;
            try {
                const result = await view.pick(x, y, {
                    sampleDiscRadius,
                    async: asyncPick,
                });

                // The gesture was abandoned while this was in flight. Applying it now
                // would move the snap indicator onto the finger, repopulate the pick
                // result so a later release places it, and re-run onDragUpdate — which
                // for point-line drags the vertex, with no undo entry.
                if (pickGenerationRef.current !== generation) {
                    return;
                }

                if (!visualOnly) {
                    lastPickRef.current = result ?? null;
                    dispatchLastPickSample(lastPickSampleActions.set(result ?? null));
                }

                // Always update the visual: when visualOnly, we handle it here regardless
                // of whether onDragUpdate is defined (since we skip onDragUpdate below).
                if (!onDragUpdate || visualOnly) {
                    const visual = determineCursorVisual(result ?? null);
                    dispatch(mobileMeasureActions.setCursorVisual(visual));
                }

                if (isPickValid(result ?? null) && result) {
                    const screenPos = view.convert.worldSpaceToScreenSpace([result.position]);
                    if (screenPos?.[0]) {
                        dispatch(mobileMeasureActions.setIndicatorPosition({ x: screenPos[0][0], y: screenPos[0][1] }));
                        const transform =
                            result.sampleType === "surface"
                                ? getSurfaceProjectionTransform(
                                      result.normalVS as [number, number, number],
                                      screenPos[0][0],
                                      screenPos[0][1],
                                      size,
                                      view,
                                  )
                                : null;
                        dispatch(mobileMeasureActions.setIndicatorTransform(transform));
                    }
                } else {
                    dispatch(mobileMeasureActions.setIndicatorPosition(null));
                    dispatch(mobileMeasureActions.setIndicatorTransform(null));
                }

                if (!visualOnly) {
                    onDragUpdate?.(result ?? null);
                }
            } finally {
                pickingRef.current = false;
            }
        },
        [
            view,
            sampleDiscRadius,
            size,
            dispatch,
            dispatchLastPickSample,
            determineCursorVisual,
            onDragUpdate,
            isPickValid,
        ],
    );

    const doPickRef = useRef(doPick);
    doPickRef.current = doPick;

    useEffect(() => {
        if (!isActive || !canvas) {
            return;
        }

        const getCanvasOffset = (touch: Touch) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top,
            };
        };

        const getMouseCanvasOffset = (e: globalThis.MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        };

        const isNearDpad = (pos: { x: number; y: number }) => {
            const dpadCenter = lastCanvasPosRef.current;
            const dpadDx = pos.x - dpadCenter.x;
            const dpadDy = pos.y - dpadCenter.y;
            const hitRadiusSq = DPAD_HIT_RADIUS * DPAD_HIT_RADIUS;
            return dpadDx * dpadDx + dpadDy * dpadDy <= hitRadiusSq;
        };

        if (persistent) {
            // ── PERSISTENT MODE ──
            // Tap = place cursor. Drag D-pad = move + release = measure. Cursor stays.

            const pickTarget = (pos: { x: number; y: number }) => ({
                x: pos.x + PICK_TARGET_OFFSET.x,
                y: pos.y + PICK_TARGET_OFFSET.y,
            });

            const placeCursor = (pos: { x: number; y: number }) => {
                cursorPlacedRef.current = true;
                lastPickRef.current = null;
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorActive(true));
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const startDrag = (pos: { x: number; y: number }) => {
                dpadOriginRef.current = { ...lastCanvasPosRef.current };
                draggingRef.current = true;
                dragStartPosRef.current = { ...pos };
                dragMovedRef.current = false;
                lastPickRef.current = null;
                dpadLongPressFiredRef.current = false;
                // Activate the cursor immediately so the sync-ref effect's !cursorActive
                // branch cannot fire during subsequent moveDrag position updates.
                dispatch(mobileMeasureActions.setCursorActive(true));
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                dispatch(mobileMeasureActions.setIsDragging(true));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
                if (onDpadLongPress) {
                    if (dpadLongPressTimerRef.current) {
                        clearTimeout(dpadLongPressTimerRef.current);
                    }
                    dpadLongPressTimerRef.current = setTimeout(() => {
                        dpadLongPressTimerRef.current = null;
                        dpadLongPressFiredRef.current = true;
                        onDpadLongPress();
                    }, DPAD_LONG_PRESS_MS);
                }
            };

            const moveDrag = (pos: { x: number; y: number }) => {
                const dragStartPos = dragStartPosRef.current;
                if (dragStartPos && !dragMovedRef.current) {
                    const dx = pos.x - dragStartPos.x;
                    const dy = pos.y - dragStartPos.y;
                    if (dx * dx + dy * dy >= DPAD_DRAG_THRESHOLD_SQ) {
                        dragMovedRef.current = true;
                        if (dpadLongPressTimerRef.current) {
                            clearTimeout(dpadLongPressTimerRef.current);
                            dpadLongPressTimerRef.current = null;
                        }
                    }
                }
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const endDrag = () => {
                if (!draggingRef.current) {
                    return;
                }
                if (dpadLongPressTimerRef.current) {
                    clearTimeout(dpadLongPressTimerRef.current);
                    dpadLongPressTimerRef.current = null;
                }
                draggingRef.current = false;
                dispatch(mobileMeasureActions.setIsDragging(false));
                const wasShortTap = !dragMovedRef.current;
                dragStartPosRef.current = null;
                if (dpadLongPressFiredRef.current && wasShortTap) {
                    return;
                }
                if (wasShortTap && dpadTapCancelsPlace && onDpadTap) {
                    onDpadTap();
                    return;
                }
                if (lastPickRef.current) {
                    onPlace(lastPickRef.current, pickTarget(lastCanvasPosRef.current));
                } else {
                    onDpadTap?.();
                }
            };

            /**
             * Gives up a gesture the hook had claimed, completing nothing. A second finger
             * means the camera is being driven, and the camera controller only learns the
             * finger count from touchstart/touchend reaching the canvas — so from here on
             * the hook must not consume those events, and must not treat the gesture as a
             * D-pad drag. Unlike `endDrag` this places no measurement and fires no tap.
             */
            const abortDrag = () => {
                if (dpadLongPressTimerRef.current) {
                    clearTimeout(dpadLongPressTimerRef.current);
                    dpadLongPressTimerRef.current = null;
                }
                dpadLongPressFiredRef.current = false;
                touchActiveRef.current = false;
                const origin = dpadOriginRef.current;
                dpadOriginRef.current = null;
                if (!draggingRef.current) {
                    return;
                }
                draggingRef.current = false;
                dragStartPosRef.current = null;
                dragMovedRef.current = false;
                lastPickRef.current = null;
                dispatch(mobileMeasureActions.setIsDragging(false));
                // Discard whatever pick is in flight and free the slot it holds, so the
                // restore pick below is not dropped as an overlapping call.
                pickGenerationRef.current += 1;
                pickingRef.current = false;
                // Cancelling the long-press timer achieves nothing once it has fired: the
                // tool has already armed a move, and only the tool can disarm it.
                onAbort?.();
                if (origin) {
                    // startDrag snapped the D-pad onto the finger; put it back so a pinch
                    // leaves no trace.
                    lastCanvasPosRef.current = origin;
                    dispatch(mobileMeasureActions.setCursorPosition(origin));
                    const pt = pickTarget(origin);
                    doPick(pt.x, pt.y, { visualOnly: true, asyncPick: true });
                }
            };

            // Document-level capture-phase handlers give the DPAD priority over
            // non-interactive overlay elements (gizmo canvas, SVG layers, labels)
            // while still letting interactive UI (buttons) handle their own events.
            const isInCanvasArea = (clientX: number, clientY: number) => {
                const rect = canvas.getBoundingClientRect();
                return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
            };

            const isInteractiveOverlay = (target: EventTarget | null) =>
                (target as HTMLElement)?.closest?.("button, [role='button']") !== null;

            // Canvas-area presses are watched without claiming the event, so the camera
            // keeps receiving them. A tap or long-press is only reported once the gesture
            // has proven to be a single finger that stayed put: anything that moves, or
            // any second finger, is a camera gesture and cancels both.
            const cancelCanvasPress = () => {
                if (canvasLongPressTimerRef.current) {
                    clearTimeout(canvasLongPressTimerRef.current);
                    canvasLongPressTimerRef.current = null;
                }
                canvasPressStartRef.current = null;
            };

            const beginCanvasPress = (pos: { x: number; y: number }) => {
                cancelCanvasPress();
                if (!onCanvasTap && !onCanvasLongPress) {
                    return;
                }
                canvasPressStartRef.current = { ...pos };
                canvasLongPressFiredRef.current = false;
                if (onCanvasLongPress) {
                    canvasLongPressTimerRef.current = setTimeout(() => {
                        canvasLongPressTimerRef.current = null;
                        canvasLongPressFiredRef.current = true;
                        onCanvasLongPress(pos);
                    }, DPAD_LONG_PRESS_MS);
                }
            };

            const trackCanvasPress = (pos: { x: number; y: number }) => {
                const start = canvasPressStartRef.current;
                if (!start) {
                    return;
                }
                const dx = pos.x - start.x;
                const dy = pos.y - start.y;
                if (dx * dx + dy * dy >= DPAD_DRAG_THRESHOLD_SQ) {
                    cancelCanvasPress();
                }
            };

            const endCanvasPress = (pos: { x: number; y: number } | null, completed: boolean) => {
                const start = canvasPressStartRef.current;
                const longPressFired = canvasLongPressFiredRef.current;
                cancelCanvasPress();
                canvasLongPressFiredRef.current = false;
                if (completed && start && !longPressFired) {
                    onCanvasTap?.(pos ?? start);
                }
            };

            const handleTouchStart = (e: TouchEvent) => {
                if (e.touches.length !== 1) {
                    // A second finger means the camera is being driven, not a tap.
                    cancelCanvasPress();
                    abortDrag();
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const touch = e.touches[0];
                const pos = getCanvasOffset(touch);

                if (isNearDpad(pos)) {
                    // Near the DPAD (placed or center-fallback) → start drag immediately.
                    touchActiveRef.current = true;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    startDrag(pos);
                } else if (cursorPlacedRef.current && isInCanvasArea(touch.clientX, touch.clientY)) {
                    // Canvas-area tap away from DPAD: run tap-elsewhere logic
                    // (e.g. select nearby laser) but let the event pass through
                    // so camera orbit still works.
                    onTapElsewhere?.(pos);
                    beginCanvasPress(pos);
                } else if (!cursorPlacedRef.current && isInCanvasArea(touch.clientX, touch.clientY)) {
                    touchActiveRef.current = true;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    placeCursor(pos);
                    onTapElsewhere?.(pos);
                }
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (e.touches.length !== 1) {
                    // Also covers a gesture already multi-touch when this effect attached.
                    cancelCanvasPress();
                    abortDrag();
                } else if (canvasPressStartRef.current) {
                    trackCanvasPress(getCanvasOffset(e.touches[0]));
                }
                if (!touchActiveRef.current || e.touches.length !== 1) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                if (draggingRef.current) {
                    moveDrag(getCanvasOffset(e.touches[0]));
                }
            };

            const handleTouchEnd = (e: TouchEvent) => {
                // touchcancel is never a tap, and a lifted finger that leaves others down
                // is part of a multi-touch gesture.
                endCanvasPress(null, e.type === "touchend" && e.touches.length === 0);
                if (e.touches.length > 0) {
                    // Consuming a lift that leaves other fingers down would hide the
                    // finger-count change from the camera controller, which only resyncs
                    // its pinch baseline on touchstart and touchend. It would then score
                    // the remaining finger against a stale two-finger midpoint and throw
                    // the camera somewhere unrelated.
                    abortDrag();
                    return;
                }
                if (touchActiveRef.current) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    touchActiveRef.current = false;
                }
                endDrag();
            };

            // Pointer handlers mirror touch logic for mouse/pen input (forceMobile).
            // Touch-originated pointer events are skipped (touch handlers take priority).
            const handlePointerDown = (e: PointerEvent) => {
                if (e.pointerType === "touch" || e.button !== 0) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const pos = getMouseCanvasOffset(e);
                if (isNearDpad(pos)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    startDrag(pos);
                } else if (cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    onTapElsewhere?.(pos);
                    beginCanvasPress(pos);
                } else if (!cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    placeCursor(pos);
                    onTapElsewhere?.(pos);
                }
            };

            const handlePointerMove = (e: PointerEvent) => {
                if (e.pointerType === "touch") {
                    return;
                }
                if (canvasPressStartRef.current) {
                    trackCanvasPress(getMouseCanvasOffset(e));
                }
                if (!draggingRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveDrag(getMouseCanvasOffset(e));
            };

            const handlePointerUp = (e: PointerEvent) => {
                if (e.pointerType === "touch") {
                    return;
                }
                endCanvasPress(getMouseCanvasOffset(e), true);
                if (!draggingRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                endDrag();
            };

            // Mouse handlers intercept engine listeners (bubble-phase).
            const handleMouseDown = (e: MouseEvent) => {
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const pos = getMouseCanvasOffset(e);
                if (isNearDpad(pos)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                } else if (!cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (draggingRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseUp = (e: MouseEvent) => {
                if (draggingRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            document.addEventListener("touchstart", handleTouchStart, { capture: true, passive: false });
            document.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
            document.addEventListener("touchend", handleTouchEnd, { capture: true, passive: false });
            document.addEventListener("touchcancel", handleTouchEnd, { capture: true, passive: false });
            document.addEventListener("pointerdown", handlePointerDown, true);
            document.addEventListener("pointermove", handlePointerMove, true);
            document.addEventListener("pointerup", handlePointerUp, true);
            document.addEventListener("mousedown", handleMouseDown, true);
            document.addEventListener("mousemove", handleMouseMove, true);
            document.addEventListener("mouseup", handleMouseUp, true);

            return () => {
                document.removeEventListener("touchstart", handleTouchStart, true);
                document.removeEventListener("touchmove", handleTouchMove, true);
                document.removeEventListener("touchend", handleTouchEnd, true);
                document.removeEventListener("touchcancel", handleTouchEnd, true);
                document.removeEventListener("pointerdown", handlePointerDown, true);
                document.removeEventListener("pointermove", handlePointerMove, true);
                document.removeEventListener("pointerup", handlePointerUp, true);
                document.removeEventListener("mousedown", handleMouseDown, true);
                document.removeEventListener("mousemove", handleMouseMove, true);
                document.removeEventListener("mouseup", handleMouseUp, true);
                cancelCanvasPress();
            };
        } else {
            // ── NON-PERSISTENT MODE (laser, coordinate, point-line) ──
            // Press → drag → release = place measurement. Cursor disappears after.

            const pickTarget = (pos: { x: number; y: number }) => ({
                x: pos.x + PICK_TARGET_OFFSET.x,
                y: pos.y + PICK_TARGET_OFFSET.y,
            });

            const activateAtPosition = (pos: { x: number; y: number }) => {
                touchActiveRef.current = true;
                lastPickRef.current = null;
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorActive(true));
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                dispatch(mobileMeasureActions.setIsDragging(true));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const moveToPosition = (pos: { x: number; y: number }) => {
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const deactivate = () => {
                if (!touchActiveRef.current) {
                    return;
                }
                touchActiveRef.current = false;
                dispatch(mobileMeasureActions.setIsDragging(false));
                if (lastPickRef.current) {
                    onPlace(lastPickRef.current, pickTarget(lastCanvasPosRef.current));
                }
                dispatch(mobileMeasureActions.setCursorActive(false));
            };

            const isInCanvasArea = (clientX: number, clientY: number) => {
                const rect = canvas.getBoundingClientRect();
                return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
            };

            const isInteractiveOverlay = (target: EventTarget | null) =>
                (target as HTMLElement)?.closest?.("button, [role='button']") !== null;

            const handleTouchStart = (e: TouchEvent) => {
                if (e.touches.length !== 1) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const touch = e.touches[0];
                if (!isInCanvasArea(touch.clientX, touch.clientY)) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                activateAtPosition(getCanvasOffset(touch));
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (!touchActiveRef.current || e.touches.length !== 1) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveToPosition(getCanvasOffset(e.touches[0]));
            };

            const handleDeactivate = (e: TouchEvent) => {
                if (touchActiveRef.current) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
                deactivate();
            };

            // Pointer handlers mirror touch logic for mouse/pen input (forceMobile).
            const handlePointerDown = (e: PointerEvent) => {
                if (e.pointerType === "touch" || e.button !== 0) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                if (isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    activateAtPosition(getMouseCanvasOffset(e));
                }
            };

            const handlePointerMove = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !touchActiveRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveToPosition(getMouseCanvasOffset(e));
            };

            const handlePointerUp = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !touchActiveRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                deactivate();
            };

            // Mouse handlers intercept engine listeners (bubble-phase).
            const handleMouseDown = (e: MouseEvent) => {
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                if (isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (touchActiveRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseUp = (e: MouseEvent) => {
                if (touchActiveRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            document.addEventListener("touchstart", handleTouchStart, { capture: true, passive: false });
            document.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
            document.addEventListener("touchend", handleDeactivate, { capture: true, passive: false });
            document.addEventListener("touchcancel", handleDeactivate, { capture: true, passive: false });
            document.addEventListener("pointerdown", handlePointerDown, true);
            document.addEventListener("pointermove", handlePointerMove, true);
            document.addEventListener("pointerup", handlePointerUp, true);
            document.addEventListener("mousedown", handleMouseDown, true);
            document.addEventListener("mousemove", handleMouseMove, true);
            document.addEventListener("mouseup", handleMouseUp, true);

            return () => {
                document.removeEventListener("touchstart", handleTouchStart, true);
                document.removeEventListener("touchmove", handleTouchMove, true);
                document.removeEventListener("touchend", handleDeactivate, true);
                document.removeEventListener("touchcancel", handleDeactivate, true);
                document.removeEventListener("pointerdown", handlePointerDown, true);
                document.removeEventListener("pointermove", handlePointerMove, true);
                document.removeEventListener("pointerup", handlePointerUp, true);
                document.removeEventListener("mousedown", handleMouseDown, true);
                document.removeEventListener("mousemove", handleMouseMove, true);
                document.removeEventListener("mouseup", handleMouseUp, true);
                touchActiveRef.current = false;
            };
        }
    }, [
        isActive,
        canvas,
        dispatch,
        doPick,
        onPlace,
        onTapElsewhere,
        onDpadTap,
        onDpadLongPress,
        onCanvasTap,
        onCanvasLongPress,
        onAbort,
        persistent,
        dpadTapCancelsPlace,
    ]);

    // Sync cursorPlacedRef when cursor is activated from outside (e.g. teleport via selectPointAsRoot)
    useEffect(() => {
        if (isActive && persistent && cursorActive && cursorPosition) {
            cursorPlacedRef.current = true;
            lastCanvasPosRef.current = cursorPosition;
        } else if (isActive && persistent && !cursorActive) {
            // cursorActive dropped to false — either initial activation (after reset())
            // or post-deletion reset. Pre-position DPAD at center and fire a visual-only
            // pick so the crosshair immediately reflects what is under screen center.
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
            const s = sizeRef.current;
            if (s.width > 0 && s.height > 0) {
                const center = { x: s.width / 2, y: s.height / 2 };
                cursorPlacedRef.current = true;
                lastCanvasPosRef.current = center;
                doPickRef.current(center.x + PICK_TARGET_OFFSET.x, center.y + PICK_TARGET_OFFSET.y, {
                    visualOnly: true,
                    asyncPick: true,
                });
            } else {
                cursorPlacedRef.current = false;
            }
        }
    }, [isActive, persistent, cursorActive, cursorPosition]);

    // Expose forceStartDrag synchronously via module-level ref so SVG long-press
    // can initiate a drag immediately (no async React effect timing issues).
    mobileMeasureCursorRef.forceStartDrag = (pos: { x: number; y: number }) => {
        cursorPlacedRef.current = true;
        draggingRef.current = true;
        dragStartPosRef.current = { ...pos };
        dragMovedRef.current = false;
        lastCanvasPosRef.current = pos;
        lastPickRef.current = null;
        dispatch(mobileMeasureActions.setIsDragging(true));
    };

    useEffect(() => {
        if (isActive && !wasActiveRef.current) {
            // Entering this picker: reset interaction state first.
            cursorPlacedRef.current = false;
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
            dispatch(mobileMeasureActions.reset());
            dispatchLastPickSample(lastPickSampleActions.set(null));

            // The sync-ref effect will fire on the next render (cursorActive → false
            // from reset()) and handle center-positioning + visual-only pick.
        } else if (!isActive && wasActiveRef.current) {
            // Leaving this picker: fully clear overlay and internal interaction refs.
            cursorPlacedRef.current = false;
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
            dispatch(mobileMeasureActions.reset());
            dispatchLastPickSample(lastPickSampleActions.set(null));
        }
        wasActiveRef.current = isActive;
    }, [isActive, dispatch, dispatchLastPickSample, persistent]);

    useEffect(() => {
        return () => {
            dispatch(mobileMeasureActions.reset());
            mobileMeasureCursorRef.forceStartDrag = null;
        };
    }, [dispatch]);

    return { cursorActive, lastPickResult: lastPickRef.current };
}
